// WhatsApp Automation product data for E Telecom.
// Prices below are business-owner supplied plan prices (do not substitute).

// Billing periods (business-confirmed): Monthly, Quarterly, Yearly.
export const WA_BILLING = {
  configured: true,
  default: "yearly",
  periods: [
    { key: "monthly", label: "Monthly", suffix: "/month" },
    { key: "quarterly", label: "Quarterly", suffix: "/quarter" },
    { key: "yearly", label: "Yearly", suffix: "/year", badge: "Best value" },
  ],
};

export const WA_TRUST = [
  { icon: "BadgeCheck", label: "Official WhatsApp Business Platform" },
  { icon: "ShieldCheck", label: "Secure Business Communication" },
  { icon: "Users", label: "Built for Growing Teams" },
];

// Ordered feature keys drive both the plan cards and the comparison table.
export const WA_FEATURE_ROWS = [
  { key: "numbers", label: "WhatsApp Business Numbers" },
  { key: "coexistence", label: "Coexistence Support", type: "bool" },
  { key: "messages", label: "Messages / Month" },
  { key: "contacts", label: "Contacts" },
  { key: "templates", label: "Templates" },
  { key: "campaigns", label: "Campaigns" },
  { key: "ruleBots", label: "Rule-Based Chatbots" },
  { key: "flowBots", label: "Flow-Based Chatbots" },
  { key: "storage", label: "Media Storage" },
  { key: "admins", label: "Admins" },
  { key: "team", label: "Team Members" },
  { key: "calls", label: "WhatsApp Calls", type: "bool" },
  { key: "aiCalling", label: "AI Calling", type: "bool" },
  { key: "api", label: "API Access", type: "bool" },
  { key: "webhooks", label: "Webhooks", type: "bool" },
];

export const WA_PLANS = [
  {
    key: "starter",
    name: "Starter",
    price: "9,999",
    prices: {
      monthly: { amount: "1,999", suffix: "/month", discount: null },
      quarterly: { amount: "4,499", suffix: "/quarter", discount: "Save 25%" },
      yearly: { amount: "9,999", suffix: "/year", discount: "Save 58%" },
    },
    desc: "For small businesses beginning WhatsApp automation.",
    recommended: false,
    summary: ["1 WhatsApp Number", "50,000 messages / month", "Unlimited campaigns", "3 rule-based chatbots"],
    features: {
      numbers: "1",
      coexistence: true,
      messages: "50,000",
      contacts: "50,000",
      templates: "Unlimited",
      campaigns: "Unlimited",
      ruleBots: "3",
      flowBots: "1",
      storage: "1 GB",
      admins: "1",
      team: "1",
      calls: false,
      aiCalling: false,
      api: false,
      webhooks: false,
    },
    notIncluded: ["WhatsApp Calls", "AI Calling", "API Access", "Webhooks"],
  },
  {
    key: "business",
    name: "Business",
    price: "14,999",
    prices: {
      monthly: { amount: "2,499", suffix: "/month", discount: null },
      quarterly: { amount: "5,499", suffix: "/quarter", discount: "Save 26%" },
      yearly: { amount: "14,999", suffix: "/year", discount: "Save 50%" },
    },
    desc: "For growing businesses that need more numbers and automation.",
    recommended: true,
    summary: ["3 WhatsApp Numbers", "1,00,000 messages / month", "API access included", "WhatsApp calls"],
    features: {
      numbers: "3",
      coexistence: true,
      messages: "1,00,000",
      contacts: "1,00,000",
      templates: "Unlimited",
      campaigns: "Unlimited",
      ruleBots: "5",
      flowBots: "3",
      storage: "2 GB",
      admins: "1",
      team: "2",
      calls: true,
      aiCalling: false,
      api: true,
      webhooks: false,
    },
    notIncluded: ["AI Calling", "Webhooks"],
  },
  {
    key: "advanced",
    name: "Advanced",
    price: "29,999",
    prices: {
      monthly: { amount: "5,999", suffix: "/month", discount: null },
      quarterly: { amount: "13,499", suffix: "/quarter", discount: "Save 25%" },
      yearly: { amount: "29,999", suffix: "/year", discount: "Save 58%" },
    },
    desc: "For larger businesses and agencies needing full automation & integrations.",
    recommended: false,
    summary: ["5 WhatsApp Numbers", "3,00,000 messages / month", "AI calling + webhooks", "Full API integration"],
    features: {
      numbers: "5",
      coexistence: true,
      messages: "3,00,000",
      contacts: "3,00,000",
      templates: "Unlimited",
      campaigns: "Unlimited",
      ruleBots: "10",
      flowBots: "5",
      storage: "2 GB",
      admins: "1",
      team: "4",
      calls: true,
      aiCalling: true,
      api: true,
      webhooks: true,
    },
    notIncluded: [],
  },
];

// Meta message charge categories with live INR list rates for India.
// Rates are sourced from Meta's published rate card (kept in a central config /
// downloadable document) and change over time — always dated.
export const META_RATE_CARD = {
  updated: "July 1, 2026",
  currency: "INR",
  country: "India",
  rateType: "List rate (lowest volume tier)",
  docUrl: "https://customer-assets-4nw71qhi.emergentagent.net/job_smart-messaging-39/artifacts/787c0cd3_INR_META_PRICING.pdf",
  referenceUrl: "https://developers.facebook.com/docs/whatsapp/pricing/",
  categories: [
    { name: "Marketing", icon: "Megaphone", desc: "Promotions, offers, product updates and re-engagement messages.", rate: "₹3.0111" },
    { name: "Utility", icon: "ReceiptText", desc: "Order updates, account notifications and post-purchase messaging.", rate: "₹0.1150" },
    { name: "Authentication", icon: "KeyRound", desc: "One-time passwords and account verification messages.", rate: "₹0.1150" },
    { name: "Authentication – International", icon: "Globe", desc: "Authentication messages to international destinations, where applicable.", rate: "₹2.4971" },
  ],
  note: "India list rates per message, effective July 1, 2026. Volume tiers apply to Utility and Authentication messages, so effective rates may be lower at higher volumes. Meta pricing is determined by Meta and may change from time to time; applicable charges depend on message category, destination and Meta's prevailing pricing policies.",
};

export const WA_USE_CASES = {
  Marketing: {
    icon: "Megaphone",
    points: ["Product launches and promotional campaigns", "Seasonal offers and announcements", "Re-engagement of opted-in customers", "Personalized, media-rich broadcasts"],
  },
  Sales: {
    icon: "Handshake",
    points: ["Instant lead qualification", "Enquiry follow-up automation", "Automatic agent assignment", "Guided product recommendations"],
  },
  Support: {
    icon: "LifeBuoy",
    points: ["Automated FAQs and self-service", "Ticket routing to the right team", "Shared inbox for faster resolution", "Conversation history at a glance"],
  },
  Operations: {
    icon: "PackageCheck",
    points: ["Order confirmations", "Delivery and dispatch updates", "Appointment and payment reminders", "Internal notifications"],
  },
  Authentication: {
    icon: "KeyRound",
    points: ["OTP delivery on WhatsApp", "Account verification flows", "Secure login confirmations", "Where supported under Meta rules"],
  },
};

export const WA_PRICING_FAQS = [
  { q: "Is E Telecom WhatsApp Automation a subscription service?", a: "Yes. WhatsApp Automation is offered as a platform subscription across Starter, Business and Advanced plans, each with different capabilities and entitlements." },
  { q: "Are Meta WhatsApp charges included in my E Telecom plan?", a: "No. Your E Telecom platform subscription and Meta's WhatsApp message charges are separate. Meta message charges depend on message category, destination and Meta's prevailing pricing." },
  { q: "Does E Telecom add markup to Meta message charges?", a: "No. Where billing runs directly on Meta rates, you pay the official Meta charges with zero E Telecom markup on those messaging charges." },
  { q: "What is WhatsApp Coexistence?", a: "Coexistence lets you continue using the WhatsApp Business app on your number while also managing conversations and automation through the E Telecom platform." },
  { q: "Can I upgrade my plan later?", a: "Yes, you can move to a higher plan as your requirements grow. Our team will help you switch to the plan that fits your usage. (Exact upgrade/downgrade terms are confirmed by our sales team.)" },
  { q: "How many WhatsApp numbers can I connect?", a: "Starter supports 1 number, Business supports 3 numbers and Advanced supports 5 numbers." },
  { q: "What happens when I reach my monthly message allowance?", a: "Message allowances vary by plan. If you expect to exceed your allowance, our team will help you choose the right plan or arrangement — please contact sales for the exact overage policy." },
  { q: "Can multiple employees use the same WhatsApp number?", a: "Yes. The shared team inbox lets multiple team members collaborate on the same WhatsApp number, with conversation assignment and ownership." },
  { q: "Does E Telecom provide API access?", a: "API access is included in the Business and Advanced plans, enabling integration with your CRM, website and internal systems where supported." },
  { q: "Which plan includes webhooks?", a: "Webhooks are available in the Advanced plan." },
  { q: "Which plan includes WhatsApp calling?", a: "WhatsApp calling is available in the Business and Advanced plans. AI Calling is available in the Advanced plan." },
  { q: "Do you provide GST invoices?", a: "Please contact our sales team for invoicing and billing details applicable to your business." },
  { q: "How long does WhatsApp Business onboarding take?", a: "Onboarding time depends on WhatsApp Business verification and your setup. Our team will share expected timelines and guide you through the process during onboarding." },
];
